import { Bubble, BubbleContent } from '@neo/primitives/bubble'
import { Button } from '@neo/primitives/button'
import { Marker, MarkerContent, MarkerIcon } from '@neo/primitives/marker'
import { Message, MessageContent, MessageFooter } from '@neo/primitives/message'
import { m } from 'framer-motion'
import { AlertCircle, Check, Copy, Loader2, RotateCw } from 'lucide-react'
import { memo, useCallback, useState } from 'react'
import { defaultRehypePlugins, Streamdown } from 'streamdown'
import { DURATION, EASE_OUT } from '../motion'
import { useVibeAgentStore } from '../store'
import {
  isRunLive,
  readAutoAnswer,
  readDraftSaveStatus,
  readToolCall,
  type TranscriptEntry,
  UPDATE_TYPES,
} from '../wire'
import { FeedbackControls } from './feedback-controls'
import { MessageMentionRenderer } from './message-mention-renderer'
import { VibeQuestionCard } from './question-card'
import { toolNameMap } from './tool-name-map'
import { useSmoothStreamText } from './use-smooth-stream-text'
import { useVibeAgentApi } from './use-vibe-agent-api'
import { useWordFade, WORD_FADE_REHYPE_PLUGIN } from './word-fade'

// Module constant: stable identity keeps Streamdown's processor cache warm and
// guarantees every message parses through the word-fade plugin.
const REHYPE_PLUGINS = [...Object.values(defaultRehypePlugins), WORD_FADE_REHYPE_PLUGIN]

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard unavailable (permissions/insecure context) — no false checkmark
    }
  }, [text])

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="vibe-hover-reveal transition-opacity p-1 rounded hover:bg-st1 text-secondary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/40"
      data-testid="button-copy-message"
    >
      {copied ? (
        <m.span
          className="block"
          initial={{ opacity: 0, transform: 'scale(0.5)' }}
          animate={{ opacity: 1, transform: 'scale(1)' }}
          transition={{ duration: DURATION.exit, ease: EASE_OUT }}
        >
          <Check className="h-3 w-3 text-foreground" />
        </m.span>
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </button>
  )
}

const UserBubble = ({ text }: { text: string }) => (
  <Message align="end" className="my-2 group">
    <MessageContent>
      <Bubble align="end" variant="muted">
        <BubbleContent className="bg-bubble-agent text-foreground p-3 rounded-lg">
          <MessageMentionRenderer content={text} />
        </BubbleContent>
      </Bubble>
      <MessageFooter className="px-0 text-xs-500">
        <CopyButton text={text} />
      </MessageFooter>
    </MessageContent>
  </Message>
)

const AiMessage = ({
  messageKey,
  text,
  streaming,
  feedbackResponseId,
}: {
  messageKey: string
  text: string
  streaming?: boolean
  /** Set only on the last bubble of a turn: enables the thumbs for that turn. */
  feedbackResponseId?: string | null
}) => {
  // Reveal is paced (network chunks arrive in bursts); copy always gets the
  // full buffered text, not the revealed slice.
  const { text: revealed, animating } = useSmoothStreamText(text, !!streaming)
  // Word-fade rides on top of the char pacing: newly revealed words fade in
  // individually while streaming; the post-stream parse strips the spans.
  // The plugin array is a module constant — Streamdown caches its processor
  // by the serialized option shape, so per-render plugin closures silently
  // collapse onto the first cached one (see word-fade.ts).
  useWordFade(messageKey, animating)

  return (
    <Message align="start" className="my-2 group">
      <MessageContent className="text-foreground">
        <Bubble variant="ghost" className="w-full max-w-full">
          <BubbleContent className="w-full max-w-full">
            <Streamdown
              className="text-p-400 vibe-agent markdown-content prose prose-sm max-w-none"
              isAnimating={animating}
              rehypePlugins={REHYPE_PLUGINS}
            >
              {revealed}
            </Streamdown>
          </BubbleContent>
        </Bubble>
        {text && !animating && (
          <MessageFooter className="justify-start self-start flex-wrap gap-0.5 px-0 text-xs-500">
            <CopyButton text={text} />
            {feedbackResponseId && <FeedbackControls responseId={feedbackResponseId} />}
          </MessageFooter>
        )}
      </MessageContent>
    </Message>
  )
}

const ToolRow = ({ entry }: { entry: TranscriptEntry }) => {
  const { toolName, clientDescription, toolArgs } = readToolCall(entry)
  // Same wording for both phases — only the icon changes: spinner while the
  // tool runs (tool_call_started), check once tool_call_completed upgrades
  // the row in place.
  const isRunning = entry.update_type === UPDATE_TYPES.TOOL_CALL_STARTED

  let displayName: string
  if (toolName === 'get_skill_instructions' && toolArgs?.skill_name) {
    displayName = `Loaded skill: ${toolArgs.skill_name}`
  } else {
    displayName = clientDescription || toolNameMap[toolName] || toolName
  }

  return (
    <Marker className="my-2 select-none">
      <MarkerContent className="text-p-400">
        {isRunning ? <span className="vibe-shimmer">{displayName}</span> : displayName}
      </MarkerContent>
      <MarkerIcon>
        {isRunning ? (
          <Loader2 className="animate-spin text-secondary" data-testid="icon-tool-running" />
        ) : (
          <m.span
            className="block"
            initial={{ opacity: 0, transform: 'scale(0.5)' }}
            animate={{ opacity: 1, transform: 'scale(1)' }}
            transition={{ duration: DURATION.exit, ease: EASE_OUT }}
          >
            <Check className="text-secondary" />
          </m.span>
        )}
      </MarkerIcon>
    </Marker>
  )
}

const AutoAnswered = ({ entry }: { entry: TranscriptEntry }) => {
  // The BE stamps the gate kind on the event (run_manager auto_event payload:
  // interaction_type "approval" | "question") — dispatch on it, never on the
  // answers shape.
  const isApproval = readAutoAnswer(entry).interactionType === 'approval'
  const content = isApproval
    ? 'Continued automatically with the recommended plan.'
    : 'Continued automatically with the recommended option.'

  // Deliberately quiet: this fires without user action, so it reads as a
  // footnote in the transcript, not an event card.
  return (
    <Marker className="my-1.5 select-none">
      <MarkerIcon className="size-3.5">
        <Check className="w-3.5 h-3.5 text-tertiary" />
      </MarkerIcon>
      <MarkerContent className="text-xs-400 text-tertiary">{content}</MarkerContent>
    </Marker>
  )
}

const ErrorBubble = ({ text }: { text: string }) => {
  const { send, resync } = useVibeAgentApi()

  // Reattach first: the run may still be alive server-side. Only a still-dead
  // run gets the last message re-sent as a fresh build.
  const handleRetry = async () => {
    await resync()
    const s = useVibeAgentStore.getState()
    if (isRunLive(s.runStatus)) return
    const lastUser = [...s.transcript].reverse().find((e) => e.role === 'user')
    if (lastUser?.text) send(lastUser.text)
  }

  return (
    <div className="my-2">
      <div className="mr-auto w-full border border-destructive/20 bg-destructive/5 rounded-2xl p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-destructive/10 rounded-full">
              <AlertCircle className="w-4 h-4 text-destructive" />
            </div>
            <span className="text-p-400 text-destructive">{text}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRetry}
            className="h-7 rounded-md"
            data-testid="button-retry-message"
          >
            <RotateCw className="w-3 h-3 mr-1" /> Retry
          </Button>
        </div>
      </div>
    </div>
  )
}

// Memoized: each AI_MESSAGE_CHUNK rebuilds the transcript array but replaces
// only the tail entry object (foldChunk / adoptUserMessage / resolveGateEntry
// keep unmatched refs), so rows other than the tail must not re-render per chunk.
export const VibeEntry = memo(
  ({ entry, showFeedback }: { entry: TranscriptEntry; showFeedback?: boolean }) => {
    if (entry.role === 'user') return <UserBubble text={entry.text ?? ''} />

    switch (entry.update_type) {
      case UPDATE_TYPES.AI_MESSAGE:
        return (
          <AiMessage
            messageKey={entry.id}
            text={entry.text ?? ''}
            streaming={entry.streaming}
            feedbackResponseId={showFeedback ? entry.response_id : null}
          />
        )
      case UPDATE_TYPES.TOOL_CALL_STARTED:
      case UPDATE_TYPES.TOOL_CALL_COMPLETED:
        return <ToolRow entry={entry} />
      case UPDATE_TYPES.ASK_USER_QUESTION:
        return <VibeQuestionCard entry={entry} />
      // waiting_for_approval never renders a row — legacy parity: the approval
      // surface is the button group (+ countdown). Old persisted transcripts may
      // still carry these entries.
      case UPDATE_TYPES.AUTO_ANSWERED:
        return <AutoAnswered entry={entry} />
      // Deliberate silence on success; only a failed save earns a marker.
      case UPDATE_TYPES.DRAFT_SAVED:
        if (readDraftSaveStatus(entry) !== 'failed') return null
        return (
          <Marker className="my-2">
            <MarkerContent className="text-p-400 text-secondary">Draft save failed.</MarkerContent>
          </Marker>
        )
      case UPDATE_TYPES.ERROR:
        return <ErrorBubble text={entry.text ?? ''} />
      default:
        return null
    }
  },
)
